/**
 * IconDisplay Component
 * For centered icons with consistent sizing
 */

import React from 'react';
import { designTokens } from '../../constants/designTokens';

interface IconDisplayProps {
  src: string;
  darkSrc?: string;
  alt: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  /** Custom width in px — overrides size class. Use with height for non-square illustrations. */
  width?: number;
  /** Custom height in px — overrides size class. Use with width for non-square illustrations. */
  height?: number;
  animated?: boolean;
  desktopScaled?: boolean;
  className?: string;
}

export const IconDisplay = React.memo(function IconDisplay({
  src,
  darkSrc,
  alt,
  size = 'xl',
  width,
  height,
  animated = false,
  desktopScaled = true,
  className = '',
}: IconDisplayProps) {
  const resolvedSrc = darkSrc
    ? document.documentElement.getAttribute('data-theme') === 'dark' ? darkSrc : src
    : src;

  const hasCustomSize = width != null && height != null;
  const iconClass = `icon-display-icon ${hasCustomSize ? 'icon-display-custom' : `icon-display-${size}`} ${desktopScaled ? 'desktop-scaled' : ''} ${animated ? 'icon-display-animated' : ''} ${className}`;

  return (
    <div className="icon-display-container">
      <img src={resolvedSrc} alt={alt} className={iconClass} />

      <style>{`
        .icon-display-container {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: ${designTokens.spacing.lg};
          
          /* Mobile-First: Ensure consistent spacing */
          min-height: 48px;
          width: 100%;
        }
        
        .icon-display-icon {
          object-fit: contain;
          
          /* Performance optimizations */
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          
          /* Mobile-First: Default to medium size */
          width: ${designTokens.components.icon.size.md};
          height: ${designTokens.components.icon.size.md};
        }
        
        /* Size variants - Mobile-First Responsive */
        .icon-display-sm {
          width: ${designTokens.components.icon.size.xs};
          height: ${designTokens.components.icon.size.xs};
        }
        
        .icon-display-md {
          width: ${designTokens.components.icon.size.sm};
          height: ${designTokens.components.icon.size.sm};
        }
        
        .icon-display-lg {
          width: ${designTokens.components.icon.size.md};
          height: ${designTokens.components.icon.size.md};
        }
        
        .icon-display-xl {
          width: ${designTokens.components.icon.size.lg};
          height: ${designTokens.components.icon.size.lg};
        }
        
        .icon-display-2xl {
          width: ${designTokens.components.icon.size.xl};
          height: ${designTokens.components.icon.size.xl};
        }
        
        .icon-display-3xl {
          width: ${designTokens.components.icon.size['2xl']};
          height: ${designTokens.components.icon.size['2xl']};
        }
        
        .icon-display-4xl {
          width: ${designTokens.components.icon.size['3xl']};
          height: ${designTokens.components.icon.size['3xl']};
        }
        
        .icon-display-5xl {
          width: ${designTokens.components.icon.size['4xl']};
          height: ${designTokens.components.icon.size['4xl']};
        }
        
        .icon-display-6xl {
          width: ${designTokens.components.icon.size['5xl']};
          height: ${designTokens.components.icon.size['5xl']};
        }
        
        /* Mobile optimizations (up to 767px) */
        ${designTokens.media.mobile} {
          .icon-display-container {
            margin-bottom: ${designTokens.spacing.sm}; /* Reduced from md */
            min-height: 32px;
          }
          
          .icon-display-3xl {
            width: ${designTokens.components.icon.size['3xl']}; /* Increased from 2xl */
            height: ${designTokens.components.icon.size['3xl']};
          }
          
          .icon-display-4xl {
            width: ${designTokens.components.icon.size['4xl']}; /* Increased from 2xl */
            height: ${designTokens.components.icon.size['4xl']};
          }
          
          .icon-display-5xl {
            width: ${designTokens.components.icon.size['4xl']}; /* Increased from 3xl */
            height: ${designTokens.components.icon.size['4xl']};
          }
          
          .icon-display-6xl {
            width: ${designTokens.components.icon.size['5xl']}; /* Increased from 4xl */
            height: ${designTokens.components.icon.size['5xl']};
          }
        }
        
        /* Tablet (768px-1023px) */
        ${designTokens.media.tablet} {
          .icon-display-container {
            margin-bottom: ${designTokens.spacing.xl};
            min-height: 64px;
          }
          
          .icon-display-sm {
            width: ${designTokens.components.icon.size.sm};
            height: ${designTokens.components.icon.size.sm};
          }
          
          .icon-display-md {
            width: ${designTokens.components.icon.size.md};
            height: ${designTokens.components.icon.size.md};
          }
          
          .icon-display-lg {
            width: ${designTokens.components.icon.size.lg};
            height: ${designTokens.components.icon.size.lg};
          }
          
          .icon-display-xl {
            width: ${designTokens.components.icon.size.xl};
            height: ${designTokens.components.icon.size.xl};
          }
          
          .icon-display-2xl {
            width: ${designTokens.components.icon.size['2xl']};
            height: ${designTokens.components.icon.size['2xl']};
          }
          
          .icon-display-3xl {
            width: ${designTokens.components.icon.size['3xl']};
            height: ${designTokens.components.icon.size['3xl']};
          }
          
          .icon-display-4xl {
            width: ${designTokens.components.icon.size['4xl']};
            height: ${designTokens.components.icon.size['4xl']};
          }
          
          .icon-display-5xl {
            width: ${designTokens.components.icon.size['5xl']};
            height: ${designTokens.components.icon.size['5xl']};
          }
          
          .icon-display-6xl {
            width: ${designTokens.components.icon.size['6xl']};
            height: ${designTokens.components.icon.size['6xl']};
          }
        }
        
        /* Desktop final sizing (1024px+, no further scaling) */
        ${designTokens.media.desktop} {
          .icon-display-container {
            margin-bottom: ${designTokens.spacing['2xl']};
          }
          
          .icon-display-3xl {
            width: ${designTokens.components.icon.size['4xl']};
            height: ${designTokens.components.icon.size['4xl']};
          }
          
          .icon-display-4xl {
            width: ${designTokens.components.icon.size['5xl']};
            height: ${designTokens.components.icon.size['5xl']};
          }
          
          .icon-display-5xl {
            width: ${designTokens.components.icon.size['5xl']};
            height: ${designTokens.components.icon.size['5xl']};
          }
          
          .icon-display-6xl {
            width: ${designTokens.components.icon.size['6xl']};
            height: ${designTokens.components.icon.size['6xl']};
          }
        }
        
        /* Desktop UX Enhancement - Simplified 3-Breakpoint System */
        .icon-display-icon.desktop-scaled {
          /* Tablet enhancement (768px-1023px) */
          ${designTokens.media.tablet} {
            &.icon-display-xl {
              width: ${designTokens.components.icon.size['2xl']}; /* 48px → 64px */
              height: ${designTokens.components.icon.size['2xl']};
            }
            
            &.icon-display-2xl {
              width: ${designTokens.components.icon.size['3xl']}; /* 64px → 80px */
              height: ${designTokens.components.icon.size['3xl']};
            }
            
            &.icon-display-3xl {
              width: ${designTokens.components.icon.size['4xl']}; /* 80px → 96px */
              height: ${designTokens.components.icon.size['4xl']};
            }
            
            &.icon-display-4xl {
              width: ${designTokens.components.icon.size['5xl']}; /* 96px → 128px */
              height: ${designTokens.components.icon.size['5xl']};
            }
          }
          
          /* Desktop final sizing (1024px+, no further scaling) */
          ${designTokens.media.desktop} {
            &.icon-display-xl {
              width: ${designTokens.components.icon.size['4xl']}; /* 48px → 96px final */
              height: ${designTokens.components.icon.size['4xl']};
            }
            
            &.icon-display-2xl {
              width: ${designTokens.components.icon.size['5xl']}; /* 64px → 128px final */
              height: ${designTokens.components.icon.size['5xl']};
            }
            
            &.icon-display-3xl {
              width: ${designTokens.components.icon.size['6xl']}; /* 80px → 144px final */
              height: ${designTokens.components.icon.size['6xl']};
            }
            
            &.icon-display-4xl {
              width: ${designTokens.components.icon.size['6xl']}; /* 96px → 144px final */
              height: ${designTokens.components.icon.size['6xl']};
            }
            
            &.icon-display-5xl {
              width: ${designTokens.components.icon.size['6xl']}; /* 128px → 144px final */
              height: ${designTokens.components.icon.size['6xl']};
            }
            
            &.icon-display-6xl {
              width: ${designTokens.components.icon.size['6xl']}; /* 144px final */
              height: ${designTokens.components.icon.size['6xl']};
            }
          }
        }
        
        /* High-DPI displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .icon-display-icon {
            image-rendering: auto;
          }
        }
        
        /* Animation */
        .icon-display-animated {
          animation: icon-pulse 2s ease-in-out infinite;
        }
        
        @keyframes icon-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
        }
        
        /* Custom size for non-square illustrations */
        ${hasCustomSize ? `
        .icon-display-custom {
          width: ${width}px;
          height: ${height}px;
          max-width: 100%;
        }

        /* Mobile: scale down proportionally */
        ${designTokens.media.mobile} {
          .icon-display-custom {
            width: ${Math.round(width * 0.6)}px;
            height: ${Math.round(height * 0.6)}px;
          }
        }

        /* Tablet: scale to 80% */
        ${designTokens.media.tablet} {
          .icon-display-custom {
            width: ${Math.round(width * 0.8)}px;
            height: ${Math.round(height * 0.8)}px;
          }
        }
        ` : ''}

        /* Reduced motion accessibility */
        @media (prefers-reduced-motion: reduce) {
          .icon-display-animated {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
});
